import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core'
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { SessionStorageService } from 'src/app/login/services/session-storage.service'
import { Utils } from '../../../../utils/utils'
import { InstallationItem } from '../../models/installation-item.model'
 
@Component({
    selector: 'app-customer-relations',
    templateUrl: './customer-relations.component.html',
    styleUrls: ['./customer-relations.component.scss'],
    standalone: false,
})
export class CustomerRelationsComponent implements AfterViewInit {
    @Input() public showAddCustomerRelationButton = true
    @Input() public isBusiness = false
    @Input() public isInToolbar: boolean = false
    @Input() public showSummary: boolean = true
    @Input() public dark: boolean = false
    @Output() public selectInstallation = new EventEmitter<InstallationItem>()
    @Output() public addCustomerRelation = new EventEmitter()
    public selectedItem: InstallationItem | null = null
    public isExpanded: boolean = false
 
    public formGroup = new UntypedFormGroup({
        installations: new UntypedFormArray([]),
    })
 
    public get installationsArray() {
        return this.formGroup.controls.installations as UntypedFormArray
    }
 
    public get installationItems(): InstallationItem[] {
        const installations = this.installationsArray
        return installations?.value ?? []
    }
 
    public get canToggleDropdown(): boolean {
        const installations = this.installationsArray
        if (!installations) {
            return false
        }
 
        if (installations.length > 1) {
            return true
        }
 
        if (installations.length === 0) {
            return false
        }
 
        const firstInstallation = installations.value?.[0]
        return !!firstInstallation && firstInstallation.installations?.length > 1
    }
 
    public get isSingleInstallationView(): boolean {
        if (!this.installationsArray?.length) {
            return false
        }
 
        const firstInstallation = this.installationsArray.value[0]
        return this.installationsArray.length === 1 && firstInstallation?.installations?.length === 1
    }
 
    public toggleExpand() {
        if (!this.canToggleDropdown) {
            return
        }
 
        this.isExpanded = !this.isExpanded
    }
 
    @Input() public set installations(installations: Array<InstallationItem>) {
        if (installations) {
            const currentInstallation = this.sessionStorageService.installationNumber
 
            this.formGroup.controls.installations = new UntypedFormArray(
                // Add id to each installation
                installations.map((item, index) => {
                    item?.installations.map((installation, id) => {
                        installation.id = id
                    })
                    let selectedInstallation
                    if (index === 0 || item.installations.length > 1) {
                        const openContract = item.installations.find((x) => x.contract.isClosed === false)
                        selectedInstallation = openContract ?? item.installations[0]
                        if (!currentInstallation) {
                            this.sessionStorageService.setSelectedInstallation({
                                customerNumber: item.customerNumber,
                                installationNumber: selectedInstallation.installationNumber,
                            })
                        }
                    }
                    return new UntypedFormControl({
                        ...item,
                        selectedInstallation,
                        id: index,
                    })
                })
            )
            // Select first installation or preselected installation from login page
            this.preselectInstallation()
        }
    }
 
    constructor(private sessionStorageService: SessionStorageService) {}
 
    public ngAfterViewInit() {
        Utils.scrollToTop()
        if (this.selectedItem) {
            this.selectInstallation.emit(this.selectedItem)
        }
    }
 
    public onSelectInstallation(selected: InstallationItem) {
        if (!selected || this.selectedItem === selected) {
            return
        }
        const single = selected.installations.length === 1 ? true : false
        const selectedInstallation = single ? selected.installations[0] : selected.selectedInstallation
 
        // Reset all selected installations if there is only one installation
        this.formGroup.controls.installations.value
            .filter((installation: any) => installation.installations.length === 1)
            .map((i: any) => (i.selectedInstallation = undefined))
 
        // Set selected installation
        this.formGroup.controls.installations.value
            .filter((installation: any) => installation.customerNumber === selected.customerNumber)
            .map((i: any) => (i.selectedInstallation = selectedInstallation))
 
        this.selectedItem = { ...selected, selectedInstallation }
 
        this.selectInstallation.emit(selected)
    }
 
    public onAddCustomerRelation() {
        this.addCustomerRelation.emit()
    }
 
    private preselectInstallation() {
        let itemToSelect = this.formGroup.controls.installations.value[0]
        const selectedCustomerNumber = this.sessionStorageService.customerNumber
        const selectedInstallationNumber = this.sessionStorageService.installationNumber
 
        if (selectedCustomerNumber) {
            const installationFound = this.formGroup.controls.installations.value.filter(
                (installations: any) => installations.customerNumber === selectedCustomerNumber
            )
            if (installationFound.length > 0) {
                const installation = installationFound[0]
 
                if (selectedInstallationNumber) {
                    const selectedInstallation = installation.installations.find(
                        (inst: any) => inst.installationNumber === selectedInstallationNumber
                    )
                    if (selectedInstallation) {
                        installation.selectedInstallation = selectedInstallation
                    }
                }

                itemToSelect = installation
            }
        }
 
        this.onSelectInstallation(itemToSelect)
    }
}